import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { decodificarPayloadJwt } from '../lib/jwt';
import type { UserRole } from '../types';

interface AuthState {
  session:    Session | null;
  user:       User    | null;
  role:       UserRole | null;
  empresaId:  string   | null;
  loading:    boolean;
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function parseJwtClaims(accessToken: string): { role: UserRole | null; empresa_id: string | null } {
  const payload = decodificarPayloadJwt(accessToken) as {
    role?: UserRole | 'authenticated';
    app_role?: UserRole;
    empresa_id?: string;
    app_metadata?: { role?: UserRole; empresa_id?: string };
  } | null;
  if (!payload) return { role: null, empresa_id: null };

  // payload.role é sempre "authenticated" no Supabase — ignorar esse valor padrão.
  // A role real vem de: app_metadata.role (sem hook) ou claim raiz injetado pelo hook.
  const rawRole = payload.role === 'authenticated' ? null : (payload.role ?? null);
  const role: UserRole | null = rawRole
    ?? payload.app_role
    ?? payload.app_metadata?.role
    ?? null;

  const empresa_id: string | null =
    payload.empresa_id
    ?? payload.app_metadata?.empresa_id
    ?? null;

  return { role, empresa_id };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session:   null,
    user:      null,
    role:      null,
    empresaId: null,
    loading:   true,
  });

  useEffect(() => {
    let ativo = true;
    // Eventos de Auth recebidos: a leitura inicial (getSession) é mais antiga que qualquer
    // evento posterior (SIGNED_OUT, TOKEN_REFRESHED...) e não pode sobrescrevê-lo ao resolver.
    let eventos = 0;

    // Carga inicial da sessão
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!ativo || eventos > 0) return;
      if (session) {
        const { role, empresa_id } = parseJwtClaims(session.access_token);
        setState({ session, user: session.user, role, empresaId: empresa_id, loading: false });
      } else {
        setState(prev => ({ ...prev, loading: false }));
      }
    });

    // Escuta mudanças de sessão (login, logout, refresh de token)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      eventos += 1;
      if (session) {
        const { role, empresa_id } = parseJwtClaims(session.access_token);
        setState({ session, user: session.user, role, empresaId: empresa_id, loading: false });
      } else {
        setState({ session: null, user: null, role: null, empresaId: null, loading: false });
      }
    });

    return () => {
      ativo = false;
      subscription.unsubscribe();
    };
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ ...state, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
